import React from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, QrCode, Scan, HandCoins, ShieldCheck, Lightning, Users, Camera, Flag, ArrowRight } from "@phosphor-icons/react";
import { PRICING, fmtR, curbMarginPerCard } from "../lib/pricing";
import { PITCH } from "../lib/pitch";

const Slide = ({ children, bg = "bg-white", className = "" }) => (
  <section className={`pitch-slide ${bg} ${className} relative w-full min-h-[100vh] sm:min-h-[800px] flex flex-col`}>
    {children}
  </section>
);

const Eyebrow = ({ children, color = "text-neutral-500" }) => (
  <div className={`label-tag ${color}`}>{children}</div>
);

const SlideNum = ({ n, total = 12 }) => (
  <div className="absolute top-6 right-6 sm:top-10 sm:right-10 font-mono-cc text-xs text-neutral-400">
    {String(n).padStart(2, "0")} / {String(total).padStart(2, "0")}
  </div>
);

export default function Pitch() {
  return (
    <div className="bg-white">
      {/* Toolbar — hidden in print */}
      <div className="no-print sticky top-0 bg-black text-white border-b border-black z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="label-tag inline-flex items-center gap-2 text-[color:var(--cc-yellow)]">
            <ArrowLeft size={14} weight="bold" /> Back to home
          </Link>
          <div className="label-tag text-neutral-300 hidden sm:block">Curb Crew · Investor &amp; Partner Deck · {PITCH.date}</div>
          <button onClick={() => window.print()} className="bg-[color:var(--cc-yellow)] text-black label-tag px-5 py-3 brutal-btn flex items-center gap-2">
            <Printer size={16} weight="bold" /> Save as PDF
          </button>
        </div>
        <div className="max-w-6xl mx-auto px-6 pb-3 text-xs text-neutral-400">
          Tip: in the print dialog, choose <strong className="text-white">"Save as PDF"</strong>, set layout to <strong className="text-white">Portrait · A4</strong>, and turn ON <strong className="text-white">"Background graphics"</strong>.
        </div>
      </div>

      <div className="pitch-deck max-w-6xl mx-auto">

        {/* 01 · Cover */}
        <Slide bg="bg-[color:var(--cc-yellow)]" className="border-b border-black">
          <SlideNum n={1} />
          <div className="flex-1 flex flex-col justify-center p-10 sm:p-20">
            <img
              src="/curb-crew-logo.svg"
              alt="Curb Crew"
              className="h-16 w-auto self-start mb-12"
            />
            <h1 className="font-display font-black tracking-tighter text-6xl sm:text-8xl leading-[0.9]">
              Smart card.<br />Scan.<br />Connect.
            </h1>
            <p className="mt-10 text-xl max-w-2xl text-neutral-800 leading-snug">
              A physical QR business card that turns South Africa's service providers into searchable, verifiable, employable professionals — without an app, a subscription, or a smartphone in their hand.
            </p>
            <div className="mt-16 flex items-end justify-between flex-wrap gap-6 border-t-2 border-black pt-6">
              <div>
                <Eyebrow color="text-black">Investor &amp; Partner Deck</Eyebrow>
                <div className="font-display font-black text-xl mt-1">{PITCH.date}</div>
              </div>
              <div className="text-right">
                <Eyebrow color="text-black">Presented by</Eyebrow>
                <div className="font-display font-black text-xl mt-1">{PITCH.founder_name}</div>
              </div>
            </div>
          </div>
        </Slide>

        {/* 02 · The Problem */}
        <Slide className="border-b border-black">
          <SlideNum n={2} />
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="p-10 sm:p-16 flex flex-col justify-center">
              <Eyebrow>The problem</Eyebrow>
              <h2 className="font-display font-black tracking-tighter text-5xl sm:text-7xl mt-3 leading-[0.95]">
                Skill is everywhere. <br /><span className="bg-[color:var(--cc-yellow)] px-2">Discovery isn't.</span>
              </h2>
              <p className="mt-8 text-lg leading-relaxed text-neutral-800">
                Millions of plumbers, electricians, painters, welders, gardeners and domestic workers in South Africa work without any way to prove their skills, build a reputation, or be found by people who need them.
              </p>
              <p className="mt-4 text-lg leading-relaxed text-neutral-800">
                Word of mouth is fragile. WhatsApp numbers get lost. Printed flyers blow away. CVs require literacy and printing costs. Online platforms require smartphones, data and digital skills most don't have.
              </p>
            </div>
            <div className="bg-black text-white p-10 sm:p-16 flex flex-col justify-center border-l border-black">
              <Eyebrow color="text-[color:var(--cc-yellow)]">By the numbers</Eyebrow>
              <div className="mt-6 space-y-6">
                {[
                  { v: "~17M", k: "South Africans in informal employment or unemployed (StatsSA)" },
                  { v: "~64%", k: "of households use word-of-mouth to find a tradesperson" },
                  { v: "<R5", k: "average daily marketing spend by an informal service provider" },
                  { v: "0", k: "tools designed for someone with skills but no smartphone" },
                ].map((x) => (
                  <div key={x.k} className="border-t border-white/30 pt-4">
                    <div className="font-display font-black text-4xl tabular-nums">{x.v}</div>
                    <div className="text-sm text-neutral-300 mt-1">{x.k}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Slide>

        {/* 03 · The Solution */}
        <Slide bg="bg-[color:var(--cc-bg-2)]" className="border-b border-black">
          <SlideNum n={3} />
          <div className="flex-1 p-10 sm:p-16 flex flex-col justify-center">
            <Eyebrow>The solution</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-7xl mt-3 leading-[0.95]">
              A physical card. <br />A digital handshake.
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-black bg-white">
              <div className="p-6 border-r border-b border-black">
                <QrCode size={36} weight="bold" />
                <div className="font-display font-black text-2xl mt-3">Hardware</div>
                <p className="text-neutral-700 mt-2">Lanyard + PVC pouch + printed QR code. Once-off cost. Worn at work.</p>
              </div>
              <div className="p-6 border-r border-b border-black">
                <Scan size={36} weight="bold" />
                <div className="font-display font-black text-2xl mt-3">Profile</div>
                <p className="text-neutral-700 mt-2">Anyone scans with any phone camera. Sees photo, name, skills, location. No app. No login.</p>
              </div>
              <div className="p-6 border-r border-b border-black">
                <HandCoins size={36} weight="bold" />
                <div className="font-display font-black text-2xl mt-3">Connect</div>
                <p className="text-neutral-700 mt-2">Visitor leaves their name + number. Cardholder's number is then revealed. WhatsApp opens with one tap.</p>
              </div>
            </div>
            <div className="mt-10 text-lg max-w-3xl text-neutral-700">
              No subscription. No app store. No data plan needed by the cardholder. Curb Crew is the cheapest CV in South Africa — and the only one that actually gets read.
            </div>
          </div>
        </Slide>

        {/* 04 · How it works */}
        <Slide className="border-b border-black">
          <SlideNum n={4} />
          <div className="flex-1 p-10 sm:p-16 flex flex-col justify-center">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-6xl mt-3 leading-[0.95]">
              From lanyard to <br /><span className="underline decoration-[color:var(--cc-yellow)] decoration-8 underline-offset-4">livelihood</span> in 4 steps.
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-4 gap-0 border-l border-t border-black">
              {[
                { n: "01", t: "Agent buys hardware", d: "Pack of 2 codes for {pack}. Buy 1, get 1 free.", icon: HandCoins },
                { n: "02", t: "Agent activates a card", d: "Photo, name, skills, SA phone, POPIA consent.", icon: Camera },
                { n: "03", t: "Cardholder wears the card", d: "Lanyard goes on. Anyone with a phone camera can scan.", icon: QrCode },
                { n: "04", t: "Visitor connects", d: "Submits name + phone. Cardholder's number is revealed. WhatsApp opens.", icon: ShieldCheck },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="p-6 border-r border-b border-black bg-white">
                    <div className="font-mono-cc text-sm text-neutral-500">{s.n}</div>
                    <Icon size={28} weight="bold" className="mt-3" />
                    <div className="font-display font-black text-xl mt-3">{s.t}</div>
                    <p className="text-neutral-700 mt-2 text-sm">
                      {s.d.replace("{pack}", fmtR(PRICING.pack_price))}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </Slide>

        {/* 05 · The Product (visual) */}
        <Slide bg="bg-black" className="text-white border-b border-black">
          <SlideNum n={5} />
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="p-10 sm:p-16 flex flex-col justify-center">
              <Eyebrow color="text-[color:var(--cc-yellow)]">The product · already built</Eyebrow>
              <h2 className="font-display font-black tracking-tighter text-5xl sm:text-6xl mt-3 leading-[0.95]">
                Live, working, <br />ready to ship.
              </h2>
              <ul className="mt-10 space-y-4 text-lg">
                <li className="border-t border-white/20 pt-3 flex justify-between"><span>Public lookup &amp; QR-scan profile</span><span className="text-[color:var(--cc-yellow)]">live</span></li>
                <li className="border-t border-white/20 pt-3 flex justify-between"><span>Agent self-signup &amp; in-field activation</span><span className="text-[color:var(--cc-yellow)]">live</span></li>
                <li className="border-t border-white/20 pt-3 flex justify-between"><span>Mandatory photo capture for identity</span><span className="text-[color:var(--cc-yellow)]">live</span></li>
                <li className="border-t border-white/20 pt-3 flex justify-between"><span>POPIA consent capture &amp; lost-card lifecycle</span><span className="text-[color:var(--cc-yellow)]">live</span></li>
                <li className="border-t border-white/20 pt-3 flex justify-between"><span>Admin code minting, allocation &amp; printable QR sheets</span><span className="text-[color:var(--cc-yellow)]">live</span></li>
                <li className="border-t border-white/20 pt-3 flex justify-between"><span>WhatsApp deep-link on contact reveal</span><span className="text-[color:var(--cc-yellow)]">live</span></li>
                <li className="border-t border-white/20 pt-3 flex justify-between"><span>CSV export for leads and cardholders</span><span className="text-[color:var(--cc-yellow)]">live</span></li>
                <li className="border-t border-white/20 pt-3 flex justify-between"><span>Installable web app (no app store needed)</span><span className="text-[color:var(--cc-yellow)]">live</span></li>
              </ul>
            </div>
            <div className="bg-[color:var(--cc-bg-2)] p-10 sm:p-16 grid place-items-center border-l border-black">
              <div className="bg-white border border-black brutal-shadow p-6 max-w-xs">
                <div className="flex items-center justify-between mb-3">
                  <span className="label-tag text-black">Curb Crew · LIVE</span>
                  <span className="font-mono-cc text-xs text-black">#CC-DEMO</span>
                </div>
                <div className="aspect-square bg-neutral-100 grid place-items-center text-neutral-400 border border-black">
                  <Camera size={56} weight="bold" />
                </div>
                <div className="mt-3">
                  <div className="font-display font-black text-xl text-black">Thabo Mokoena</div>
                  <div className="label-tag text-neutral-600 mt-1">Plumber · Electrician</div>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  <span className="label-tag bg-[color:var(--cc-yellow)] px-2 py-1 border border-black text-black">Geyser</span>
                  <span className="label-tag bg-[color:var(--cc-yellow)] px-2 py-1 border border-black text-black">Drains</span>
                </div>
                <div className="mt-4 bg-black text-white text-center label-tag py-2">
                  Request contact number
                </div>
              </div>
            </div>
          </div>
        </Slide>

        {/* 06 · Unit Economics */}
        <Slide className="border-b border-black">
          <SlideNum n={6} />
          <div className="flex-1 p-10 sm:p-16 flex flex-col justify-center">
            <Eyebrow>Unit economics</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-7xl mt-3 leading-[0.95]">
              Simple retail. <br /><span className="bg-[color:var(--cc-yellow)] px-2">Healthy unit economics.</span>
            </h2>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-black">
              {[
                { who: "Cardholder pays", v: fmtR(PRICING.cardholder_price), note: "Once-off. No subscription.", bg: "bg-white" },
                { who: "Curb Crew net", v: fmtR(curbMarginPerCard()), note: `${fmtR(PRICING.hardware_cost_per_card)} hardware cost absorbed.`, bg: "bg-black text-white" },
              ].map((x) => (
                <div key={x.who} className={`p-8 border-r border-b border-black ${x.bg}`}>
                  <div className="label-tag opacity-70">{x.who}</div>
                  <div className="font-display font-black text-6xl mt-3 tabular-nums">{x.v}</div>
                  <div className="text-sm mt-3 opacity-80">{x.note}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 text-lg">
              <div className="border-l-4 border-black pl-4">
                <strong>Cost stack:</strong> Lanyard {fmtR(13)} + Printing {fmtR(2)} = {fmtR(PRICING.hardware_cost_per_card)} hardware. PostNet shipping {fmtR(PRICING.shipping_fee)} amortised over orders of {PRICING.free_shipping_threshold}+ codes.
              </div>
              <div className="border-l-4 border-[color:var(--cc-yellow)] pl-4">
                <strong>Launch deal:</strong> "Buy 1, get 1 FREE." Customer pays {fmtR(PRICING.pack_price)} for {PRICING.pack_size} codes.
              </div>
            </div>
          </div>
        </Slide>

        {/* 07 · The Numbers (scale) */}
        <Slide bg="bg-[color:var(--cc-bg-2)]" className="border-b border-black">
          <SlideNum n={7} />
          <div className="flex-1 p-10 sm:p-16 flex flex-col justify-center">
            <Eyebrow>The numbers · at scale</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-6xl mt-3 leading-[0.95]">
              Volume is the moat.
            </h2>

            <div className="mt-10 overflow-x-auto bg-white border border-black">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b-2 border-black bg-black text-white">
                    <th className="text-left label-tag p-4">Activated cards / yr</th>
                    <th className="text-right label-tag p-4">Cardholder revenue</th>
                    <th className="text-right label-tag p-4">Wholesale revenue</th>
                    <th className="text-right label-tag p-4 bg-[color:var(--cc-yellow)] text-black">Curb Crew net</th>
                  </tr>
                </thead>
                <tbody>
                  {[1000, 5000, 25000, 100000].map((n) => (
                    <tr key={n} className="border-b border-neutral-200">
                      <td className="p-4 font-display font-black text-lg">{n.toLocaleString("en-ZA")}</td>
                      <td className="p-4 text-right tabular-nums">{fmtR(n * PRICING.cardholder_price)}</td>
                      <td className="p-4 text-right tabular-nums">{fmtR(n * (PRICING.pack_price / PRICING.pack_size))}</td>
                      <td className="p-4 text-right tabular-nums font-display font-black bg-[color:var(--cc-yellow)]/30">{fmtR(n * curbMarginPerCard())}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 text-sm text-neutral-600">
              Excludes shipping. Bulk orders ({PRICING.free_shipping_threshold}+ codes) include free PostNet delivery; smaller orders pay {fmtR(PRICING.shipping_fee)} on top.
            </div>
          </div>
        </Slide>

        {/* 08 · Compliance & Trust */}
        <Slide className="border-b border-black">
          <SlideNum n={8} />
          <div className="flex-1 p-10 sm:p-16 flex flex-col justify-center">
            <Eyebrow>Compliance &amp; trust</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-6xl mt-3 leading-[0.95]">
              Legally clean. <br />Ethically sound.
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-black">
              {[
                { icon: ShieldCheck, t: "Not a recruitment agency", d: "Curb Crew sells a digital business card product, not job placements. We do not match, place or take commission on hires. Sits outside the Employment Services Act 2014." },
                { icon: Flag, t: "POPIA-compliant by design", d: "Explicit cardholder consent captured at activation for name, photo, skills, location and bio. Phone numbers are NEVER public — revealed only on visitor request." },
                { icon: Camera, t: "Identity-verified cards", d: "Mandatory photo per card. Employers match face to person at the door. Deters fraud and lost-card misuse." },
                { icon: Users, t: "Lost-card lifecycle", d: "Anyone can flag a lost card. Profile auto-deactivates. Code can be re-issued. No personal info exposed to bad actors." },
              ].map((x) => {
                const Icon = x.icon;
                return (
                  <div key={x.t} className="p-6 border-r border-b border-black bg-white">
                    <Icon size={28} weight="bold" />
                    <div className="font-display font-black text-xl mt-3">{x.t}</div>
                    <p className="text-neutral-700 mt-2 text-sm leading-relaxed">{x.d}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </Slide>

        {/* 09 · Market Opportunity */}
        <Slide bg="bg-[color:var(--cc-yellow)]" className="border-b border-black">
          <SlideNum n={9} />
          <div className="flex-1 p-10 sm:p-16 flex flex-col justify-center">
            <Eyebrow color="text-black">Market opportunity</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-7xl mt-3 leading-[0.95]">
              Every taxi rank. <br />Every hardware store. <br />Every street corner.
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-black bg-white">
              {[
                { v: "~7M", k: "Self-employed / informal service providers in SA" },
                { v: "~R8B", k: "Annual informal services market (gardening, plumbing, building, domestic)" },
                { v: "1%", k: "Capture rate at scale = R56M annual cardholder revenue" },
              ].map((x) => (
                <div key={x.k} className="p-6 border-r border-b border-black">
                  <div className="font-display font-black text-5xl tabular-nums">{x.v}</div>
                  <div className="text-sm text-neutral-700 mt-2">{x.k}</div>
                </div>
              ))}
            </div>
            <div className="mt-10 text-lg max-w-3xl text-neutral-800">
              South Africa first. Then SADC: Botswana, Namibia, Zimbabwe and Mozambique share the same informal-economy structure. The product travels.
            </div>
          </div>
        </Slide>

        {/* 10 · Go-to-Market */}
        <Slide className="border-b border-black">
          <SlideNum n={10} />
          <div className="flex-1 p-10 sm:p-16 flex flex-col justify-center">
            <Eyebrow>Go-to-market</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-6xl mt-3 leading-[0.95]">
              Agents do the walking. <br />Curb Crew prints the cards.
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-0 border-l border-t border-black bg-white">
              {[
                { n: "PHASE 1", t: "Pilot · 30 cards", d: "Direct retail via PostNet + online. Validate activation and retention.", time: "Month 1" },
                { n: "PHASE 2", t: "Retail · 500 cards", d: "Hardware shops, taxi-rank stand kiosks, township spaza networks. Bulk pack tier launches.", time: "Months 2–3" },
                { n: "PHASE 3", t: "Scale · 10,000 cards", d: "Geographic expansion: Cape Town, Durban, Pretoria. Referral bonuses go live.", time: "Months 4–12" },
              ].map((p) => (
                <div key={p.n} className="p-6 border-r border-b border-black">
                  <div className="font-mono-cc text-xs text-neutral-500">{p.n}</div>
                  <div className="font-display font-black text-xl mt-2">{p.t}</div>
                  <p className="text-neutral-700 mt-3 text-sm leading-relaxed">{p.d}</p>
                  <div className="label-tag mt-4 bg-[color:var(--cc-yellow)] inline-block px-2 py-1 border border-black">{p.time}</div>
                </div>
              ))}
            </div>
            <div className="mt-10 text-lg max-w-3xl text-neutral-700">
              "Buy 1, get 1 FREE" is the retail hook. <strong>Free PostNet delivery on orders of {PRICING.free_shipping_threshold}+ codes</strong> is the upsell mechanism — pulls early buyers into bulk economics fast.
            </div>
          </div>
        </Slide>

        {/* 11 · Roadmap */}
        <Slide bg="bg-[color:var(--cc-bg-2)]" className="border-b border-black">
          <SlideNum n={11} />
          <div className="flex-1 p-10 sm:p-16 flex flex-col justify-center">
            <Eyebrow>Roadmap</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-6xl mt-3 leading-[0.95]">
              Built today. <br />Bigger tomorrow.
            </h2>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white border border-black p-6">
                <Eyebrow color="text-black">Shipped</Eyebrow>
                <ul className="mt-4 space-y-2 text-sm">
                  <li>· QR profile + lookup</li>
                  <li>· Agent + admin dashboards</li>
                  <li>· Photo upload + identity</li>
                  <li>· POPIA consent + disclaimer</li>
                  <li>· Lost-card lifecycle</li>
                  <li>· WhatsApp click-to-chat</li>
                  <li>· Printable QR sheets</li>
                  <li>· CSV export</li>
                  <li>· Installable web app (PWA)</li>
                </ul>
              </div>
              <div className="bg-[color:var(--cc-yellow)] border border-black p-6">
                <Eyebrow color="text-black">Next 3 months</Eyebrow>
                <ul className="mt-4 space-y-2 text-sm">
                  <li>· Verified reviews via job-card code</li>
                  <li>· Agent referral bonuses</li>
                  <li>· Online pack purchase (Yoco/Stripe)</li>
                  <li>· Custom domain + branding</li>
                  <li>· Bulk QR PDF generator</li>
                  <li>· Multi-language: isiZulu, Afrikaans, English</li>
                </ul>
              </div>
              <div className="bg-black text-white border border-black p-6">
                <Eyebrow color="text-[color:var(--cc-yellow)]">Year 2+</Eyebrow>
                <ul className="mt-4 space-y-2 text-sm">
                  <li>· SADC expansion</li>
                  <li>· Skills certifications integration</li>
                  <li>· Card-holder payment ratings</li>
                  <li>· Insurance partner cross-sell</li>
                  <li>· Banking partner: instant accounts</li>
                  <li>· Sector-specific cards (security, hospitality)</li>
                </ul>
              </div>
            </div>
          </div>
        </Slide>

        {/* 12 · The Ask + Contact */}
        <Slide bg="bg-black" className="text-white">
          <SlideNum n={12} />
          <div className="flex-1 p-10 sm:p-16 flex flex-col justify-center">
            <Eyebrow color="text-[color:var(--cc-yellow)]">The ask</Eyebrow>
            <h2 className="font-display font-black tracking-tighter text-5xl sm:text-7xl mt-3 leading-[0.95]">
              Let's get the <br />first 1,000 cards <br /><span className="bg-[color:var(--cc-yellow)] text-black px-2">on the streets.</span>
            </h2>
            <div className="mt-10 max-w-3xl text-lg text-neutral-300 leading-relaxed">
              {PITCH.the_ask}
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-white/30">
              <div className="p-6 border-r border-b border-white/30">
                <Eyebrow color="text-[color:var(--cc-yellow)]">Use of funds · indicative</Eyebrow>
                <ul className="mt-4 space-y-2">
                  <li className="flex justify-between border-b border-white/10 pb-2"><span>Hardware (1,000 lanyards + pouches)</span><span className="font-mono-cc">{fmtR(15000)}</span></li>
                  <li className="flex justify-between border-b border-white/10 pb-2"><span>Agent recruitment + training</span><span className="font-mono-cc">{fmtR(20000)}</span></li>
                  <li className="flex justify-between border-b border-white/10 pb-2"><span>Marketing + signage</span><span className="font-mono-cc">{fmtR(15000)}</span></li>
                  <li className="flex justify-between border-b border-white/10 pb-2"><span>Legal (POPIA + ESA review)</span><span className="font-mono-cc">{fmtR(10000)}</span></li>
                  <li className="flex justify-between pb-2"><span>Working capital + buffer</span><span className="font-mono-cc">{fmtR(40000)}</span></li>
                </ul>
              </div>
              <div className="p-6 border-r border-b border-white/30">
                <Eyebrow color="text-[color:var(--cc-yellow)]">Contact</Eyebrow>
                <div className="mt-4 space-y-3">
                  <div>
                    <div className="text-xs text-neutral-400">Founder</div>
                    <div className="font-display font-black text-xl">{PITCH.founder_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-400">Email</div>
                    <div className="font-mono-cc">{PITCH.contact_email}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-400">Phone</div>
                    <div className="font-mono-cc">{PITCH.contact_phone}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-400">Entity</div>
                    <div>{PITCH.company_legal} · {PITCH.city}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-12 flex items-center gap-3">
              <img
                src="/curb-crew-logo.svg"
                alt="Curb Crew"
                className="h-10 w-auto"
              />
              <span className="ml-auto label-tag text-neutral-400">Smart card. Scan. Connect.</span>
            </div>
          </div>
        </Slide>
      </div>

      {/* Print rules — one slide per A4 page */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          @page { size: A4 portrait; margin: 0; }
          body { background: white; print-color-adjust: exact; -webkit-print-color-adjust: exact; }
          .pitch-deck { max-width: none !important; }
          .pitch-slide {
            page-break-after: always;
            break-after: page;
            min-height: 297mm !important;
            border-bottom: none !important;
          }
          .pitch-slide:last-child {
            page-break-after: auto;
          }
        }
      `}</style>
    </div>
  );
}
